import {
  DownloadOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Flex,
  InputRef,
  Tooltip,
  Table,
  TableProps,
} from "antd";
import PageHeading from "components/PageHeading.component";
import StatBand from "components/StatBand.component";
import { getAllProductsByBusinessId } from "hooks/queries/product";
import { useOpenCreateFromNav } from "hooks/useOpenCreateFromNav";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { useTabParam } from "hooks/useTabParam";
import {
  comparePromoStatus,
  PromoStatus,
  PromoStatusBadge,
  promoStatusOf,
  promoStatusWord,
} from "pages/products/component/PromoStatusBadge";
import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { th, tnum } from "shared/components/TableText";
import PATHS from "shared/constants/paths";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { PAGE_SIZE } from "shared/constants";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { PackageProduct, ProductType } from "shared/types";
import { formatCurrency } from "shared/utils";
import { downloadCatalogueCsv } from "shared/utils/catalogue-csv";

// "5" or "1-10" (numeric ranges, not sentences).
const range = (min: number | null, max: number | null) => {
  if (min === null || max === null) return "-";
  return min === max ? String(min) : `${min}-${max}`;
};
const priceRange = (min: number | null, max: number | null) => {
  if (min === null || max === null) return "-";
  return min === max
    ? formatCurrency(min)
    : `${formatCurrency(min)} - ${formatCurrency(max)}`;
};

// Band segments (formerly the Reguler/Promo Tabs, plus a Semua view the tabs
// never had). "reguler" stays the default so every fresh visit still shows
// the curated catalog, not the accumulated promo history.
const PACKAGE_VIEWS = ["semua", "reguler", "promo"] as const;

// Credit products only — memberships live on their own sibling page under
// Produk (pages/products/membership.tsx), mirroring the sidebar split.
const PackagesPage = () => {
  const navigate = useNavigate();
  const { isRestricted } = useAdminCapabilities();
  const catalogRestricted = isRestricted(AdminCapability.CatalogPricing);
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getAllProductsByBusinessId();
  const creditProducts = useMemo(
    () =>
      data?.products.filter((p) => p.product_type === ProductType.Credit) ?? [],
    [data],
  );
  // Semua/Reguler/Promo split: promo packages (including already-ended ones
  // — is_promo never gets cleared once a promo ends, see the Name column's
  // "Berakhir" tag below) never disappear on their own, so without this
  // split the table only grows more mixed over time. Defaults to Reguler so
  // every fresh visit shows the curated catalog, not the accumulated promo
  // history. An in-page ?tab= sub-view (useTabParam) driven by the stat
  // band, not a separate sidebar route — that's a different pattern from
  // the Paket/Membership split, which WAS tried as in-page tabs and
  // deliberately reverted after review in favor of two pages; this stays
  // one page/one table, just with its dataSource split by is_promo.
  const { activeKey, onChange: onViewChange } = useTabParam(
    "reguler",
    "tab",
    PACKAGE_VIEWS,
  );
  const regularProducts = useMemo(
    () => creditProducts.filter((p) => !p.is_promo),
    [creditProducts],
  );
  // Aktif before Mendatang before Berakhir by default, and within each group
  // the most urgent/recent item first (see comparePromoStatus) — an admin
  // managing live promos cares about the one ending soonest, not whichever
  // sorts first alphabetically, and ended ones only accumulate over time
  // (is_promo never clears once a promo ends). This is the default row
  // order, not a locked sort — clicking a column header still re-sorts
  // normally.
  const promoProducts = useMemo(
    () => [...creditProducts].filter((p) => p.is_promo).sort(comparePromoStatus),
    [creditProducts],
  );
  const visibleProducts =
    activeKey === "promo"
      ? promoProducts
      : activeKey === "semua"
        ? creditProducts
        : regularProducts;
  // Constant-keyed calls, not one call with a dynamic `packages-${activeKey}`
  // key — useRememberedTablePage's page number comes from useState's lazy
  // initializer, which only runs on mount. This component doesn't unmount
  // when the view changes, so a dynamic key would silently carry over
  // whatever page number was already in that one state slot from the
  // previous view (e.g. land on Promo already "on page 3" just because
  // that's where Reguler happened to be). AntD's Table clamps an
  // out-of-range page to the last valid one, which is what was masking this
  // — it never rendered visibly blank the way membership.tsx's manual
  // card-grid pagination did, but the underlying bug was the same.
  const semuaPagination = useRememberedTablePage("packages-semua");
  const regularPagination = useRememberedTablePage("packages-reguler");
  const promoPagination = useRememberedTablePage("packages-promo");
  const { page, handleTableChange } =
    activeKey === "promo"
      ? promoPagination
      : activeKey === "semua"
        ? semuaPagination
        : regularPagination;

  // Dashboard hero quick action: go straight to the create page.
  useOpenCreateFromNav(() => {
    navigate(`${PATHS.PACKAGE_NEW}?type=credit`);
  });

  // Only meaningful on the Promo tab — a Reguler row is never is_promo, so
  // this column would just be a page of "-" there; spliced into `columns`
  // conditionally below rather than always present.
  const promoStatusColumn: NonNullable<TableProps<PackageProduct>["columns"]>[number] =
    {
      title: th(translate("product_catalog.col_status_promo")),
      dataIndex: "promo_active",
      key: "promo_status",
      render: (_, product) => <PromoStatusBadge product={product} />,
      sorter: comparePromoStatus,
      filters: (["active", "upcoming", "ended"] as PromoStatus[]).map((status) => ({
        value: status,
        text: promoStatusWord(status),
      })),
      onFilter: (value, record) => promoStatusOf(record) === value,
    };

  const columns: TableProps<PackageProduct>["columns"] = [
    {
      title: th(translate("label.name")),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
      // Reguler defaults to alphabetical; Promo already arrives pre-sorted
      // (Aktif → Mendatang → Berakhir, see promoProducts above) — forcing
      // this column's own alphabetical default there would immediately
      // re-sort it away. Clicking the header still works normally either way.
      defaultSortOrder: activeKey === "promo" ? undefined : "ascend",
      // Spread before `render` so the column's own render below is the one
      // the Table sees.
      ...getColumnSearchProps<PackageProduct, "name">({
        config: "name",
        searchInput,
      }),
      render: (_, product) => {
        const ended = promoStatusOf(product) === "ended";
        return (
          <span
            style={{
              fontWeight: 600,
              color: ended ? colors.ink3 : undefined,
            }}
          >
            {product.name}
          </span>
        );
      },
    },
    // Only meaningful on the Promo tab — see promoStatusColumn above.
    ...(activeKey === "promo" ? [promoStatusColumn] : []),
    {
      title: th(translate("class")),
      dataIndex: "class_type",
      key: "class_type",
      render: (_, product) => {
        if (product.class_type.length === 0) return "-";
        if (product.class_type.length > 1) {
          return (
            <Tooltip title={product.class_type.map((c) => c.name).join(", ")}>
              <strong>{product.class_type.length}</strong> {translate("class")}
            </Tooltip>
          );
        }
        return product.class_type[0].name;
      },
      filterSearch: true,
      filters: [
        { value: "private", text: "Private" },
        { value: "open", text: "Open" },
      ],
      onFilter: (value, record) =>
        record.class_type.flatMap((cls) => cls.type).includes(value.toString()),
    },
    {
      title: th(translate("product_catalog.col_tiers")),
      dataIndex: "tier_count",
      key: "tier_count",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>{product.tier_count}</span>
      ),
      sorter: (a, b) => a.tier_count - b.tier_count,
    },
    {
      title: th(translate("product_catalog.col_credit_range")),
      dataIndex: "credit_min",
      key: "credit_range",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>{range(product.credit_min, product.credit_max)}</span>
      ),
      sorter: (a, b) => (a.credit_min ?? 0) - (b.credit_min ?? 0),
    },
    {
      title: th(translate("product_catalog.col_price_range")),
      dataIndex: "price_min",
      key: "price_range",
      align: "right",
      render: (_, product) => (
        <span style={tnum}>{priceRange(product.price_min, product.price_max)}</span>
      ),
      sorter: (a, b) => (a.price_min ?? 0) - (b.price_min ?? 0),
    },
  ];

  return (
    <>
      <PageHeading title={translate("package")} />
      <Flex vertical gap={16}>
        {/* The catalog views, formerly Tabs: each segment shows that slice
            of the table (re-tap a slice to widen back to Semua). Counts are
            product counts, so the band doubles as the page's timeless
            facts. */}
        <StatBand
          segments={[
            {
              key: "semua",
              value: creditProducts.length,
              label: translate("list_header.all"),
            },
            {
              key: "reguler",
              value: regularProducts.length,
              label: translate("product_catalog.tab_reguler"),
            },
            {
              key: "promo",
              value: promoProducts.length,
              label: translate("product_catalog.tab_promo"),
            },
          ]}
          selectedKey={activeKey}
          onSelect={(key) => onViewChange(key === activeKey ? "semua" : key)}
        />
        {(data?.ungrouped_count ?? 0) > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={`${data?.ungrouped_count} ${translate("product_catalog.ungrouped_warning")}`}
          />
        )}
        <Card styles={{ body: { padding: 18 } }}>
          <Flex justify="flex-end" gap={8} style={{ marginBottom: 14 }}>
            {/* Exports EVERY product (memberships included) so the admin gets
                one lookup table for id_paket, whichever catalogue page they
                are on — see shared/utils/catalogue-csv. */}
            <Button
              icon={<DownloadOutlined />}
              disabled={!data?.products.length}
              onClick={() => downloadCatalogueCsv(data?.products ?? [])}
            >
              {translate("product_catalog.export_catalogue")}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={catalogRestricted}
              onClick={() => navigate(`${PATHS.PACKAGE_NEW}?type=credit`)}
            >
              {translate("product_catalog.add_credit")}
            </Button>
          </Flex>
          <Table
            showSorterTooltip={false}
            // Remount on tab change — antd's `defaultSortOrder` only seeds
            // the Table's internal sort state on its FIRST mount and never
            // re-reads it afterward, so without this, landing on Reguler
            // first (Name column's defaultSortOrder="ascend") permanently
            // latches alphabetical sorting into the Table even after
            // switching to Promo (whose Name column has no default order),
            // silently overriding comparePromoStatus's grouped order. A
            // fresh key resets that internal state so each tab actually
            // gets its own default sort again.
            key={activeKey}
            rowKey="id"
            size="middle"
            loading={isLoading}
            columns={columns}
            dataSource={visibleProducts}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => navigate(`${PATHS.PACKAGE}/${record.id}`),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      </Flex>
    </>
  );
};

export default PackagesPage;
